
import React from 'react';

export const LogoMarquee: React.FC = () => {
  // Enterprise/Institutional partners for "Backed By" section
  const logos = [
    { name: "C10 LABS", custom: true, type: 'c10' },
    { name: "EUROPEAN COMMISSION", custom: true, type: 'eu' },
    { name: "STATION F", custom: true, type: 'stationf' },
    { name: "CALLING 2 SCALE", custom: true, type: 'calling2scale' },
    { name: "K67", custom: true, type: 'k67' },
    { name: "INSEAD", custom: true, type: 'insead' },
    { name: "EIT", custom: true, type: 'eit' },
    { name: "INSEAD LAUNCHPAD", custom: true, type: 'insead_launchpad' }
  ];

  return (
    <section className="py-20 bg-brandBg overflow-hidden border-y border-zodiac/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10 flex items-center justify-center">
        <div className="flex items-center space-x-4">
          <div className="h-px w-8 bg-zodiac/10"></div>
          <p className="text-[10px] font-black text-zodiac/30 uppercase tracking-[0.6em]">Strategic Partners</p>
          <div className="h-px w-8 bg-zodiac/10"></div>
        </div>
      </div>
      
      <div className="relative w-full flex overflow-hidden group">
        {/* Edge Fades for smooth transition and premium depth */}
        <div className="absolute left-0 top-0 bottom-0 w-40 bg-gradient-to-r from-brandBg to-transparent z-10"></div>
        <div className="absolute right-0 top-0 bottom-0 w-40 bg-gradient-to-l from-brandBg to-transparent z-10"></div>

        <div className="flex animate-marquee group-hover:[animation-play-state:paused] whitespace-nowrap items-center py-4">
          {/* Repeating the array to ensure a smooth, continuous loop */}
          {[...logos, ...logos, ...logos, ...logos].map((logo, idx) => (
            <div 
              key={idx} 
              className="flex items-center justify-center px-16 md:px-24 transition-all duration-700 cursor-default grayscale opacity-30 hover:opacity-100 hover:grayscale-0"
            >
              {logo.custom && (
                <>
                  {logo.type === 'c10' ? (
                    <div className="flex items-center text-xl md:text-2xl font-black tracking-[0.3em] select-none">
                      <span className="text-[#FF0000]">C 1 0</span>
                      <span className="text-[#7B848B] ml-8">L A B S</span>
                    </div>
                  ) : logo.type === 'eu' ? (
                    <div className="flex items-center select-none scale-90 md:scale-100">
                      <div className="relative flex items-end">
                        <svg width="60" height="40" viewBox="0 0 60 40" className="absolute -top-6 left-0 text-zodiac opacity-20 group-hover:text-[#4B4B4B] transition-colors">
                          <path d="M0 20 Q 15 5, 30 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
                          <path d="M5 22 Q 20 8, 35 22" fill="none" stroke="currentColor" strokeWidth="0.5" />
                          <path d="M10 24 Q 25 11, 40 24" fill="none" stroke="currentColor" strokeWidth="0.5" />
                        </svg>
                        <div className="relative w-14 h-9 bg-[#003399] flex items-center justify-center shadow-sm">
                          <svg viewBox="0 0 120 80" width="100%" height="100%">
                            {[...Array(12)].map((_, i) => {
                              const angle = (i * 30) * (Math.PI / 180);
                              const x = 60 + 20 * Math.cos(angle);
                              const y = 40 + 20 * Math.sin(angle);
                              return (
                                <polygon key={i} points="0,-4 1,-1 4,0 1,1 0,4 -1,1 -4,0 -1,-1" transform={`translate(${x},${y})`} fill="#FFCC00" />
                              );
                            })}
                          </svg>
                        </div>
                        <div className="ml-4 flex flex-col justify-center border-r-2 border-[#003399] pr-4 leading-tight">
                          <span className="text-[#4B4B4B] text-[11px] font-bold tracking-tight">European</span>
                          <span className="text-[#4B4B4B] text-[11px] font-bold tracking-tight">Commission</span>
                        </div>
                      </div>
                    </div>
                  ) : logo.type === 'stationf' ? (
                    <div className="flex items-center select-none group/stf">
                      <svg width="220" height="40" viewBox="0 0 280 50" fill="currentColor">
                        <defs>
                          <mask id="stencilMask">
                            <rect width="100%" height="100%" fill="white" />
                            <rect x="0" y="0" width="1" height="100%" fill="black" transform="translate(140, 0)" />
                            <rect x="25" y="0" width="1.5" height="100%" fill="black" />
                            <rect x="58" y="0" width="1.5" height="100%" fill="black" />
                            <rect x="88" y="0" width="1.5" height="100%" fill="black" />
                            <rect x="118" y="0" width="1.5" height="100%" fill="black" />
                            <rect x="146" y="0" width="1.5" height="100%" fill="black" />
                            <rect x="178" y="0" width="1.5" height="100%" fill="black" />
                            <rect x="210" y="0" width="1.5" height="100%" fill="black" />
                            <rect x="254" y="0" width="1.5" height="100%" fill="black" />
                          </mask>
                        </defs>
                        <text x="0" y="40" mask="url(#stencilMask)" className="font-black text-[42px] tracking-[0.05em]" style={{ fontFamily: 'Inter, sans-serif' }}>STATION F</text>
                      </svg>
                    </div>
                  ) : logo.type === 'calling2scale' ? (
                    <div className="flex items-center select-none scale-90 md:scale-100">
                      <div className="relative w-48 h-16 flex items-center">
                        <svg viewBox="0 0 200 100" className="w-full h-full">
                          {/* Circular background */}
                          <circle cx="50" cy="50" r="42" fill="#112248" />
                          {/* Outer lime ring */}
                          <circle cx="50" cy="50" r="46" fill="none" stroke="#8CC63F" strokeWidth="2.5" />
                          
                          {/* Rays on the right */}
                          <line x1="102" y1="42" x2="118" y2="35" stroke="#8CC63F" strokeWidth="2.5" strokeLinecap="round" />
                          <line x1="105" y1="52" x2="122" y2="52" stroke="#8CC63F" strokeWidth="2.5" strokeLinecap="round" />
                          <line x1="102" y1="62" x2="118" y2="69" stroke="#8CC63F" strokeWidth="2.5" strokeLinecap="round" />
                          
                          {/* Text Re-creation */}
                          <text x="50" y="44" textAnchor="middle" fill="white" className="font-bold text-[22px]" style={{ fontFamily: 'Inter, sans-serif' }}>Calling</text>
                          <text x="32" y="72" textAnchor="middle" fill="#8CC63F" className="font-black text-[32px]" style={{ fontFamily: 'Inter, sans-serif' }}>2</text>
                          <text x="74" y="72" textAnchor="middle" fill="white" className="font-bold text-[28px]" style={{ fontFamily: 'Inter, sans-serif' }}>Scale</text>
                        </svg>
                      </div>
                    </div>
                  ) : logo.type === 'k67' ? (
                    <div className="flex items-end select-none group/k67">
                      <span className="text-4xl md:text-5xl font-black tracking-tighter text-black leading-none">K67</span>
                      <div className="ml-3 flex flex-col items-start leading-[0.8] mb-0.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.25em] text-black">Tech</span>
                        <span className="text-[11px] font-black uppercase tracking-[0.25em] text-black">Hub</span>
                      </div>
                    </div>
                  ) : logo.type === 'insead' ? (
                    <div className="flex items-center select-none group/insead scale-90 md:scale-100">
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col items-center">
                          <span className="text-[#006648] text-[22px] font-serif font-medium tracking-tight mb-0.5">INSEAD</span>
                          <svg width="64" height="6" viewBox="0 0 64 6" fill="none" className="text-[#006648]">
                            <path d="M2 1 Q 32 6, 62 1" stroke="currentColor" strokeWidth="1" fill="none" />
                            <path d="M30 3.5 L 32 5 L 34 3.5" stroke="currentColor" strokeWidth="1" fill="none" />
                          </svg>
                        </div>
                        <div className="relative w-16 h-16 bg-[#006648] rounded-full flex flex-col items-center justify-center">
                          <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-8 bg-brandBg rounded-full transition-colors duration-700"></div>
                          <div className="relative z-10 flex flex-col items-start leading-[0.95] translate-x-1.5">
                            <span className="text-white text-[9px] font-black uppercase tracking-widest">AI</span>
                            <span className="text-white text-[9px] font-black uppercase tracking-widest">VENTURE</span>
                            <span className="text-white text-[9px] font-black uppercase tracking-widest">LAB</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : logo.type === 'eit' ? (
                    <div className="flex items-center select-none group/eit scale-90 md:scale-100">
                      <div className="flex items-center">
                        <div className="relative w-16 h-16 flex items-center justify-center mr-4">
                          <svg viewBox="0 0 100 100" className="w-full h-full transform rotate-[-15deg]">
                            <path d="M 50 10 A 40 40 0 1 1 10 50" fill="none" stroke="#003399" strokeWidth="8" strokeLinecap="round" />
                            <path d="M 50 25 A 25 25 0 1 1 25 50" fill="none" stroke="#8CC63F" strokeWidth="8" strokeLinecap="round" />
                          </svg>
                          <span className="absolute text-[#003399] font-bold text-xl tracking-tighter">eit</span>
                        </div>
                        <div className="h-10 w-px bg-[#003399]/20 mx-1"></div>
                        <div className="ml-4 flex flex-col leading-none">
                          <span className="text-[#003399] text-[15px] font-medium tracking-tight">European Institute of</span>
                          <span className="text-[#003399] text-[15px] font-medium tracking-tight mt-1">Innovation & Technology</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center select-none group/launchpad scale-90 md:scale-100">
                      <div className="relative flex items-center mb-1">
                        <svg className="absolute top-[-14px] left-[-8px] w-32 h-6 text-[#99CC66]" viewBox="0 0 100 20">
                          <path d="M 0 18 Q 50 2, 100 18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                        <div className="flex items-baseline font-black text-4xl tracking-tighter">
                          <span className="text-[#006648]">launch</span>
                          <span className="text-[#99CC66]">pad</span>
                        </div>
                        <svg className="absolute bottom-[-16px] left-[50%] w-40 h-8 text-[#99CC66]" viewBox="0 0 150 20">
                          <path d="M 0 5 Q 75 25, 150 5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                        <div className="ml-3 text-[#99CC66] transform rotate-[15deg]">
                          <svg width="42" height="42" viewBox="0 0 24 24" fill="currentColor">
                             <path d="M13.13 2.188c-4.444 0-8.048 3.603-8.048 8.047 0 1.25.285 2.433.793 3.493L3.13 18.474c-.4.4-.4 1.047 0 1.447.2.2.46.3.724.3s.524-.1.724-.3l4.745-2.744c1.06.508 2.243.793 3.493.793 4.444 0 8.047-3.603 8.047-8.047.001-4.444-3.602-8.047-8.046-8.047zm.805 6.035c-.888 0-1.61-.721-1.61-1.61 0-.888.721-1.61 1.61-1.61.888 0 1.61.721 1.61 1.61 0 .889-.722 1.61-1.61 1.61z" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex items-center text-[#112248]/40 font-light text-[14px] tracking-tight uppercase">
                        <span>LaunchPad @ STATION F</span>
                        <span className="ml-1 font-bold text-[16px] mb-0.5">&gt;</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
